// BeaconPromoDisplay.jsx  →  src/components/Display/BeaconPromoDisplay.jsx
// Magic Beacon promo for the DISPLAY (1280x800, touch). Replaces
// BeaconComingDisplay on the BAND__upgrade route (non-addon devices).
// Same patterns as BeaconComingDisplay: hides the global BottomBar while
// open, X returns to Home, Watch the Demo uses the identical pop-out video.
// One layout; the arrows swap the clip + pink line + text (SLIDES below).
//
// "Claim My MagicBeacon" opens BeaconClaimDisplay (the order page). While it's
// open, this page's clips and animations are fully unmounted.
//
// TV: same page with a remote layer — lime focus ring, arrows move, OK
// presses, Back exits. Next Feature is focused on open. The demo video opens
// with its ✕ focused so OK (or Back) closes it.

import React, { useEffect, useRef, useState, useCallback } from "react";
import useGlobalStore from "../../store/global.store";
import { useRemote } from "../../hooks/useRemote";
import { useTV } from "../../hooks/useTV";
import { useConfig } from "../../hooks/useConfig";
import BeaconClaimDisplay from "./BeaconClaimDisplay";

// ── SET THIS ────────────────────────────────────────────────────────────────
// Folder where the files from beacon-promo-assets.zip are served.
// Default assumes they're copied into your app's public/beacon-promo/ folder.
const ASSET_BASE = '/beacon-promo/';
// 'mp4' = hardware-decoded video (try first). 'gif' = display-sized GIFs,
// same approach as last year's VIP page. Switch here if MP4 misbehaves.
const CLIP_FORMAT = 'mp4';
// 'lite' = only the main clip moves. The Beacon circle is a still photo and the
// never-ending effects (rainbow title, Claim ring/shine/breathe, arrow pulse)
// are off. 'full' = everything animated. Use 'lite' while tracking down the
// display crash; turn effects back on one at a time once it's stable.
const MOTION = 'full';
// Beacon circle: true = animated clip, false = still photo (one less moving image).
const CIRCLE_ANIMATED = true;
const DEMO_VIDEO_URL = "https://cdn.mywaitserver.live/beaconpromo720.mp4"; // display (same as BeaconComingDisplay)
const DEMO_VIDEO_URL_TV = "https://cdn.mywaitserver.live/beaconpromo1080_1.mp4"; // TV
const LIME = "#3ADF3C";
// ────────────────────────────────────────────────────────────────────────────

const STAGE_W = 1280;
const STAGE_H = 800;

const COLORS = {
  pink: '#F033D7',
  ring: '#ED2C93',
  blue: '#007BFF',
  text: '#F4F2EE',
  night: '#0A0B14',
};

const RAINBOW = ['#FF5C93', '#FF8A3D', '#FFD23D', '#B6E04A', '#3EE08A', '#3CC9FF', '#6FB0FF', '#8F9BFF', '#B48CFF', '#E07CFF', '#FF6FC8'];

// Sparkle burst for the Secret Sale unlock (positions only; one-shot).
const SPARKS = Array.from({ length: 16 }, (_, k) => {
  const a = (k / 16) * Math.PI * 2;
  const r = 260 + (k % 3) * 70;
  return { dx: Math.round(Math.cos(a) * r), dy: Math.round(Math.sin(a) * r * 0.7), c: RAINBOW[k % RAINBOW.length], d: (k % 4) * 0.05 };
});

// Clips are MP4 (H.264): the device decodes these in hardware, unlike
// animated WebP/GIF which are decoded frame-by-frame on the CPU. Each clip
// has a .jpg poster (its first frame) with the same name.
const FILES = {
  night: 'night.jpg',
  hero: 'hero_sync',
  spin: 'beacon_spin',
  sync: 'sync_show',
  tink: 'tink_alert',
  color: 'color_design',
  alerts: 'timers_alerts',
  closer: 'pullback_closer',
};

// ── Slides ──────────────────────────────────────────────────────────────────
// One layout. The arrows only swap the clip, the pink line and the text.
// Title, Beacon circle, Watch the Demo and Claim never change.
const SLIDES = [
  {
    id: 'magic',
    clip: 'hero',
    alt: 'MagicBands changing color with fireworks on a TV',
    line1: 'Your MagicBands.',
    pink: 'Synced to your {device}.',
    textSize: 22,
    demoTop: 494,
    text: "Simply plug the MagicBeacon into your Wait Time {device}'s USB port and watch your bands light up, vibrate and react in real time to the action on your screen.",
  },
  {
    id: 'live',
    clip: 'sync',
    alt: 'MagicBands lighting up in sync with a live fireworks show',
    line1: 'Your MagicBands.',
    pink: 'Sync to LIVE shows.',
    textSize: 20,
    demoTop: 560,
    text: 'Choose any LIVE fireworks show, from any streamer in the Magical MultiView or from our LIVE Close-Up Fireworks Camera and our system automatically detects what you’re watching and syncs your bands LIVE to the show. Every color. Every flash. Every vibration. Perfectly timed, just like you’re standing on Main Street.',
  },
  {
    id: 'tink',
    clip: 'tink',
    alt: 'A family on the couch with their MagicBands glowing as pixie dust sparkles',
    line1: 'Your MagicBands.',
    pink: 'Glow when Tink flies.',
    textSize: 22,
    demoTop: 462,
    text: 'Turn on Tink Alert and your bands will flash and vibrate the moment Tink takes flight over the castle each night!',
  },
  {
    id: 'color',
    clip: 'color',
    alt: 'Tapping green on the screen and a MagicBand lighting up green',
    line1: 'Your MagicBands.',
    pink: 'Any color you want.',
    textSize: 22,
    demoTop: 494,
    text: 'Tap any color and your bands light up to match. Or create your own light designs and color combinations to illuminate every band in the room.',
  },
  {
    id: 'alerts',
    clip: 'alerts',
    alt: 'Tapping Love You Mom on the display and the message lighting up a MagicBand',
    line1: 'Your MagicBands.',
    pink: 'Part of your every day.',
    textSize: 22,
    demoTop: 494,
    text: 'Make your MagicBands part of your everyday routine with timers and alerts. Buzz the kids when it’s time for dinner, or send a loved one a kiss goodnight.',
  },
  {
    id: 'setup',
    clip: 'closer',
    alt: 'A MagicBand glowing with the fireworks, pulling back to a couple watching on the couch',
    line1: 'Your MagicBands.',
    pink: 'They just work.',
    textSize: 22,
    demoTop: 528,
    text: 'No band activation, no app pairing, no Bluetooth. Put on any charged MagicBand and it just works. Friends coming over? Their bands light up too, turning your home into your own theme park experience.',
  },
];

// ── Styles (scoped with mbp- prefix; no blur anywhere) ──────────────────────
const CSS = `
.mbp-root{position:fixed;inset:0;z-index:9999;background:${COLORS.night};overflow:hidden;font-family:'Lato','Segoe UI',system-ui,sans-serif;-webkit-tap-highlight-color:transparent}
.mbp-stage{position:absolute;left:50%;top:50%;width:${STAGE_W}px;height:${STAGE_H}px;transform-origin:50% 50%;overflow:hidden;color:${COLORS.text}}
.mbp-stage *{box-sizing:border-box}
.mbp-btn{font-family:inherit;cursor:pointer;border:0;padding:0;margin:0;color:inherit;background:none;-webkit-appearance:none;appearance:none;touch-action:manipulation}
.mbp-btn:focus-visible{outline:3px solid #FFFFFF;outline-offset:4px}
.mbp-press{transition:transform .12s ease}
.mbp-press:active{transform:scale(.96)}
.mbp-claim.mbp-press:active{transform:none}

.mbp-fade{animation:mbpFade .45s ease both}
@keyframes mbpFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}

/* Claim button: moving rainbow ring + slow breathe + shine sweep */
.mbp-claimWrap{position:absolute;border-radius:999px;padding:5px;overflow:hidden;-webkit-mask-image:-webkit-radial-gradient(white,black);animation:mbpBreathe 2.6s ease-in-out infinite;will-change:transform}
.mbp-claimWrap::before{content:'';position:absolute;top:0;left:0;width:300%;height:100%;background:linear-gradient(90deg,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF,#FF5C93);animation:mbpRing 5.5s linear infinite;will-change:transform}
.mbp-claim{position:relative;z-index:1;overflow:hidden;width:100%;height:100%;border-radius:999px;background:${COLORS.blue};color:#FFFFFF;font-size:24px;font-weight:900;letter-spacing:.2px;display:flex;align-items:center;justify-content:center;gap:12px}
.mbp-claim::after{content:'';position:absolute;top:0;left:0;width:28%;height:100%;background:linear-gradient(100deg,rgba(255,255,255,0) 0%,rgba(255,255,255,.55) 50%,rgba(255,255,255,0) 100%);transform:translateX(-150%) skewX(-20deg);animation:mbpShine 3.4s ease-in-out infinite;will-change:transform}
@keyframes mbpRing{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes mbpBreathe{0%,100%{transform:scale(1)}50%{transform:scale(1.035)}}
@keyframes mbpShine{0%,55%{transform:translateX(-150%) skewX(-20deg)}85%,100%{transform:translateX(450%) skewX(-20deg)}}

/* Moving rainbow text (title word) */
.mbp-rainbowText{background-image:linear-gradient(90deg,${RAINBOW.join(',')},${RAINBOW[0]});background-size:200% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent;animation:mbpRainbowText 6s linear infinite}
@keyframes mbpRainbowText{from{background-position:0% 50%}to{background-position:-200% 50%}}

/* Swap: the clip pushes the old one out (direction-aware), the pink line
   wipes in, the text rises in. Transform/opacity/clip-path only. */
.mbp-clip{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;will-change:transform,opacity}
.mbp-clipIn-next{animation:mbpInNext .7s cubic-bezier(.65,0,.35,1) both}
.mbp-clipOut-next{animation:mbpOutNext .7s cubic-bezier(.65,0,.35,1) both}
.mbp-clipIn-prev{animation:mbpInPrev .7s cubic-bezier(.65,0,.35,1) both}
.mbp-clipOut-prev{animation:mbpOutPrev .7s cubic-bezier(.65,0,.35,1) both}
@keyframes mbpInNext{from{transform:translateX(100%) scale(.92);opacity:.4}to{transform:none;opacity:1}}
@keyframes mbpOutNext{from{transform:none;opacity:1}to{transform:translateX(-60%) scale(.88);opacity:0}}
@keyframes mbpInPrev{from{transform:translateX(-100%) scale(.92);opacity:.4}to{transform:none;opacity:1}}
@keyframes mbpOutPrev{from{transform:none;opacity:1}to{transform:translateX(60%) scale(.88);opacity:0}}
.mbp-swapLine{display:inline-block;animation:mbpSwapLine .75s cubic-bezier(.2,.7,.2,1) .1s both}
@keyframes mbpSwapLine{from{opacity:0;transform:translateX(-24px)}to{opacity:1;transform:none}}
.mbp-swapText{animation:mbpSwapText .6s cubic-bezier(.2,.7,.2,1) .25s both}
@keyframes mbpSwapText{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
.mbp-dot{width:10px;height:10px;border-radius:999px;background:rgba(255,255,255,.35);transition:width .3s ease,background .3s ease}
.mbp-dot.on{width:28px;background:#FFFFFF}

/* Next arrow: solid white disc, nudging chevron, pulsing ring */
.mbp-next{position:absolute;height:68px;padding:0 20px 0 28px;gap:8px;border-radius:999px;background:#FFFFFF;color:${COLORS.blue};display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:900;white-space:nowrap}
.mbp-next svg{animation:mbpNudge 1.6s ease-in-out infinite}
.mbp-next::before{content:'';position:absolute;inset:-4px;border-radius:999px;border:3px solid #FFFFFF;animation:mbpPulse 1.8s ease-out infinite}
@keyframes mbpNudge{0%,100%{transform:translateX(0)}50%{transform:translateX(5px)}}
@keyframes mbpPulse{0%{transform:scale(1);opacity:.85}100%{transform:scale(1.45);opacity:0}}



/* ── Demo video: ported verbatim from BeaconComingDisplay (bcd- → mbp-) ── */
.mbp-video-root{position:fixed;inset:0;z-index:9990;pointer-events:auto;background:rgba(0,0,0,0.88);display:grid;place-items:center}
.mbp-video-card{position:relative;z-index:10000;width:min(96vw,1300px);aspect-ratio:16 / 9;max-height:min(88vh,760px);border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.6);background:#000}
.mbp-video{width:100%;height:100%;object-fit:cover;object-position:center center;display:block;background-color:#000}
.mbp-video-close{position:fixed;top:16px;right:24px;z-index:10001;font-size:56px;color:white;background:none;border:none;cursor:pointer;font-family:'Lato',sans-serif}
.mbp-unmute-btn{position:absolute;right:12px;bottom:12px;z-index:10002;padding:10px 14px;border-radius:8px;border:0;background:rgba(0,0,0,.65);color:#fff;font-family:'Lato',sans-serif;font-weight:700;font-size:14px;cursor:pointer}

/* Lite motion: no never-ending effects (see MOTION) */
.mbp-lite .mbp-claimWrap{animation:none;-webkit-mask-image:none}
.mbp-lite .mbp-claimWrap::before{animation:none;width:100%;background:linear-gradient(90deg,#FF5C93,#FF8A3D,#FFD23D,#3EE08A,#3CC9FF,#8F7CFF)}
.mbp-lite .mbp-claim::after{animation:none;display:none}
.mbp-lite .mbp-rainbowText{animation:none;background-size:100% 100%}
.mbp-lite .mbp-next svg{animation:none}
.mbp-lite .mbp-next::before{animation:none;display:none}

/* ── Secret Sale unlock: everything plays once; only the SHOP NOW ring loops ── */
.mbp-un-root{position:absolute;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;background:rgba(6,7,16,.97);overflow:hidden;animation:mbpUnFade .35s ease both}
@keyframes mbpUnFade{from{opacity:0}to{opacity:1}}
.mbp-un-rays{position:absolute;left:50%;top:50%;width:1400px;height:1400px;margin:-700px 0 0 -700px;border-radius:50%;background:repeating-conic-gradient(from 0deg,rgba(255,210,61,.18) 0deg 8deg,rgba(255,210,61,0) 8deg 22deg);opacity:0;animation:mbpUnRays 5s ease-out 2.2s both;pointer-events:none}
.mbp-un-rays::after{content:'';position:absolute;inset:0;border-radius:50%;background:radial-gradient(circle,rgba(6,7,16,0) 8%,rgba(6,7,16,1) 60%)}
@keyframes mbpUnRays{0%{opacity:0;transform:rotate(0deg) scale(.6)}15%{opacity:1}100%{opacity:.6;transform:rotate(40deg) scale(1)}}
.mbp-un-flash{position:absolute;inset:0;background:#FFFFFF;opacity:0;pointer-events:none;z-index:5;animation:mbpUnFlash .7s ease-out 2.2s both}
@keyframes mbpUnFlash{0%{opacity:0}15%{opacity:.85}100%{opacity:0}}
.mbp-un-lock{position:absolute;left:50%;top:50%;width:150px;height:205px;margin:-102px 0 0 -75px;z-index:3;pointer-events:none;animation:mbpUnLock 2.6s ease both}
@keyframes mbpUnLock{0%{transform:scale(.3);opacity:0}10%{transform:scale(1.08);opacity:1}15%{transform:scale(1)}22%{transform:rotate(-9deg)}27%{transform:rotate(9deg)}32%{transform:rotate(-9deg)}37%{transform:rotate(9deg)}42%{transform:rotate(-5deg)}47%{transform:rotate(0deg)}60%{transform:scale(1.06)}85%{transform:scale(1);opacity:1}100%{transform:scale(1.8);opacity:0}}
.mbp-un-shackle{animation:mbpUnShackle .35s ease-out 1.45s both}
@keyframes mbpUnShackle{from{transform:translateY(0)}to{transform:translateY(-16px)}}
.mbp-un-spark{position:absolute;left:50%;top:50%;width:14px;height:14px;margin:-7px 0 0 -7px;border-radius:50%;opacity:0;z-index:1;pointer-events:none;animation:mbpUnSpark 1.1s cubic-bezier(.1,.7,.3,1) both}
@keyframes mbpUnSpark{0%{opacity:1;transform:translate(0,0) scale(.5)}70%{opacity:1}100%{opacity:0;transform:translate(var(--dx),var(--dy)) scale(1.2)}}
.mbp-un-card{position:relative;z-index:2;width:1000px;text-align:center;opacity:0;animation:mbpUnPop .6s cubic-bezier(.2,1.4,.4,1) 2.3s both}
@keyframes mbpUnPop{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:none}}
.mbp-un-title{font-size:76px;font-weight:900;letter-spacing:-1px;line-height:1.05;background-image:linear-gradient(90deg,#FFB800,#FFF3B0,#FFD23D,#FFB800);-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent}
.mbp-un-sub{max-width:900px;margin:34px auto 0;font-size:31px;font-weight:700;color:#FFFFFF;line-height:1.4}
.mbp-un-note{max-width:780px;margin:34px auto 0;padding:18px 28px;border-radius:16px;border:2px solid rgba(240,51,215,.6);background:rgba(240,51,215,.10);font-size:22px;font-weight:700;line-height:1.5;color:#FFFFFF}
.mbp-un-no{margin-top:30px;padding:8px 16px;border-radius:12px;color:rgba(255,255,255,.78);font-size:22px;font-weight:700;text-decoration:underline}

@media (prefers-reduced-motion: reduce){
  .mbp-claimWrap,.mbp-claimWrap::before,.mbp-claim::after,.mbp-next svg,.mbp-next::before,.mbp-fade,.mbp-rainbowText,.mbp-clipIn-next,.mbp-clipOut-next,.mbp-clipIn-prev,.mbp-clipOut-prev,.mbp-swapLine,.mbp-swapText{animation:none}
}
`;

function useFitScale() {
  const calc = () => Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H);
  const [scale, setScale] = useState(calc);
  useEffect(() => {
    const onResize = () => setScale(calc());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return scale;
}

// Muted, looping, inline video. muted is set on the element itself because
// some WebViews only allow autoplay when the muted property is really set.
function LoopVideo({ src, poster, label, className, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || CLIP_FORMAT === 'gif') return;
    v.muted = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    // On unmount, empty the element so the hardware decoder is released.
    return () => {
      try { v.pause(); v.removeAttribute('src'); v.load(); } catch (e) {}
    };
  }, [src]);
  if (CLIP_FORMAT === 'gif') {
    return <img className={className} style={style} src={src} alt={label} draggable={false} />;
  }
  return (
    <video
      ref={ref}
      className={className}
      style={style}
      src={src}
      poster={poster}
      aria-label={label}
      muted
      autoPlay
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      controls={false}
    />
  );
}

function Rainbow({ children }) {
  return <span className="mbp-rainbowText">{children}</span>;
}

// ── Main component ──────────────────────────────────────────────────────────
export default function BeaconPromoDisplay({ onClaim, assetBase = ASSET_BASE }) {
  const { updateView } = useRemote();
  const { isTv } = useTV();
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const scale = useFitScale();
  const [i, setI] = useState(0);
  const slide = SLIDES[i];
  const deviceWord = isTv ? 'TV' : 'Display';
  const fill = (t) => t.replace('{device}', deviceWord);
  const hasNext = SLIDES.length > 1; // wraps around in both directions
  const hasPrev = i > 0; // no going back from the first state
  const src = useCallback((key) => assetBase + FILES[key], [assetBase]);
  const vid = useCallback((key) => assetBase + FILES[key] + (CLIP_FORMAT === 'gif' ? '.gif' : '.mp4'), [assetBase]);
  const poster = useCallback((key) => assetBase + FILES[key] + '.jpg', [assetBase]);
  const [outgoing, setOutgoing] = useState(null); // { idx, dir } of the clip sliding away
  const [dir, setDir] = useState('next');
  const go = (n, d) => { setOutgoing({ idx: i, dir: d }); setDir(d); setI(n); };
  const goNext = () => hasNext && go((i + 1) % SLIDES.length, 'next');
  const goPrev = () => hasPrev && go((i - 1 + SLIDES.length) % SLIDES.length, 'prev');

  // Demo video state — same logic as BeaconComingDisplay.
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mutedFallback, setMutedFallback] = useState(false);
  const [pendingUnmutedAttempt, setPendingUnmutedAttempt] = useState(false);

  // Hide the global BottomBar while the promo is open (PhotoFrame pattern).
  useEffect(() => {
    setShowBottomBar(false);
    return () => setShowBottomBar(true);
  }, [setShowBottomBar]);

  // Preload the media so the first frame shows instantly.
  useEffect(() => {
    // Only the small poster images are preloaded; videos stream as needed.
    Object.keys(FILES).forEach((k) => { const im = new Image(); im.src = k === 'night' ? src(k) : poster(k); });
  }, [src, poster]);

  useEffect(() => {
    if (!showVideo || !pendingUnmutedAttempt) return;
    const v = videoRef.current;
    if (!v) return;
    (async () => {
      try {
        v.muted = false;
        v.volume = 0.5;
        await v.play();
        setMutedFallback(false);
        setIsPlaying(true);
      } catch {
        try {
          v.muted = true;
          await v.play();
          setMutedFallback(true);
          setIsPlaying(true);
        } catch {
          setIsPlaying(false);
        }
      } finally {
        setPendingUnmutedAttempt(false);
      }
    })();
  }, [showVideo, pendingUnmutedAttempt]);

  const openVideo = (e) => {
    if (e) e.stopPropagation();
    setPendingUnmutedAttempt(true);
    setShowVideo(true);
  };

  const closeVideo = () => {
    setShowVideo(false);
    setIsPlaying(false);
    setMutedFallback(false);
  };

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    updateView("Home");
  };

    const [showClaim, setShowClaim] = useState(false);
  const handleClaim = () => { if (onClaim) onClaim(); setShowClaim(true); };

  // RTDB gate: config/Buttons/beacon_notifyme (true = "Notify Me" + Secret Sale unlock).
  // beacon_notifyme_Override = activation codes that get it while the flag is false.
  const { config } = useConfig();
  const device = useGlobalStore((state) => state.device);
  const notifyOn = (() => {
    const b = config?.Buttons;
    if (!b) return false;
    if (b.beacon_notifyme === true) return true;
    const ov = b.beacon_notifyme_Override;
    const id = device?.subscription?._id;
    if (!id || !ov) return false;
    if (Array.isArray(ov)) return ov.includes(id);
    if (typeof ov === "string") return ov === id;
    if (typeof ov === "object") return Object.values(ov).includes(id);
    return false;
  })();
    // One-time early access: either button marks it used (saved on this device).
  const EARLY_KEY = 'wsBeaconEarlyUsed';
  const [earlyUsed, setEarlyUsed] = useState(() => { try { return localStorage.getItem(EARLY_KEY) === '1'; } catch (e) { return false; } });
  const markEarlyUsed = () => { setEarlyUsed(true); try { localStorage.setItem(EARLY_KEY, '1'); } catch (e) {} };
  const [showUnlock, setShowUnlock] = useState(false);
  const chimeRef = useRef(null);
  const handleClaimBtn = () => {
    if (!notifyOn) { handleClaim(); return; }
    if (earlyUsed) { handleClaim(); return; } // already unlocked: straight to claim, no animation
    try { chimeRef.current = new Audio('/static/connection-chime.mp3'); chimeRef.current.load(); } catch (e) {}
    setShowUnlock(true);
  };
  const handleUnlockShop = () => { markEarlyUsed(); setShowUnlock(false); handleClaim(); };
  const closeUnlock = () => { markEarlyUsed(); setShowUnlock(false); };
    
  // Chime when the shackle pops open (same 1.45s as .mbp-un-shackle).
  useEffect(() => {
    if (!showUnlock) return;
    const t = setTimeout(() => {
      try { const a = chimeRef.current; if (a) { a.currentTime = 0; const p = a.play(); if (p && p.catch) p.catch(() => {}); } } catch (e) {}
    }, 1450);
    return () => clearTimeout(t);
  }, [showUnlock]);

  // ===== TV remote layer =====
  const [focusId, setFocusId] = useState("next");
    const layout = showUnlock
    ? [["ushop"], ["uclose"]]
    : showVideo
    ? [["vclose"]]
    : [["close"], ["demo", ...(hasPrev ? ["prev"] : []), "next"], ["claim"]];
  const tvActions = {
    close: () => handleClose(),
    demo: () => openVideo(),
    prev: goPrev,
    next: goNext,
    claim: handleClaimBtn,
    vclose: closeVideo,
    ushop: handleUnlockShop,
    uclose: closeUnlock,
  };
  // Video opens → focus its ✕. Video closes → back to Next Feature.
  useEffect(() => {
  if (isTv) setFocusId(showUnlock ? "ushop" : showVideo ? "vclose" : "next");
  }, [isTv, showVideo, showUnlock]);
  // Leaving slide 0 → Prev appears; if focus was on a now-missing button, reset.
  useEffect(() => {
    if (isTv && !layout.some((row) => row.includes(focusId))) setFocusId("next");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, hasPrev]);

  const navRef = useRef({});
  navRef.current = { layout, tvActions, focusId, showVideo, showClaim, showUnlock };
  useEffect(() => {
    if (!isTv) return;
    const onKey = (e) => {
      const { layout: lay, tvActions: act, focusId: fid, showVideo: vid, showClaim: claimOpen, showUnlock: unl } = navRef.current;
      if (claimOpen) return; // the Claim page has its own remote layer
      if (document.querySelector(".wsanntv-root")) return; // announcement popup is up: it owns the remote
      const k = e.key;
      const isBack = k === "Escape" || k === "GoBack" || k === "BrowserBack" || e.keyCode === 461 || e.keyCode === 10009;
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      if (k === "ArrowUp" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowRight") {
        stop();
        let r = lay.findIndex((row) => row.includes(fid));
        if (r < 0) { setFocusId(lay[0][0]); return; }
        let c = lay[r].indexOf(fid);
        if (k === "ArrowLeft") c = Math.max(0, c - 1);
        if (k === "ArrowRight") c = Math.min(lay[r].length - 1, c + 1);
        if (k === "ArrowUp" || k === "ArrowDown") {
          const nr = Math.max(0, Math.min(lay.length - 1, r + (k === "ArrowDown" ? 1 : -1)));
          if (nr !== r) {
            // Into the middle row → land on Next Feature (the main action).
            c = lay[nr].includes("next") ? lay[nr].indexOf("next") : 0;
            r = nr;
          }
        }
        setFocusId(lay[r][c]);
        return;
      }
      if (k === "Enter" || k === " ") { stop(); if (act[fid]) act[fid](); return; }
      if (isBack) { stop(); if (unl) act.uclose(); else if (vid) act.vclose(); else act.close(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isTv]);
  const ring = (id) => (isTv && focusId === id ? { boxShadow: `0 0 0 5px ${LIME}` } : {});

  // Order page: render ONLY it, so nothing from the promo keeps running.
  if (showClaim) {
    return <BeaconClaimDisplay onBack={() => setShowClaim(false)} assetBase={assetBase} />;
  }

  return (
    <div className={'mbp-root' + (MOTION === 'lite' ? ' mbp-lite' : '')} role="dialog" aria-modal="true" aria-label="MagicBeacon"
      style={{ backgroundImage: `${isTv ? 'linear-gradient(rgba(10,11,20,.35), rgba(10,11,20,.35)), ' : ''}radial-gradient(ellipse at 50% 40%, rgba(10,11,20,0) 55%, rgba(10,11,20,.55) 100%), url(${src('night')})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>      <style>{CSS}</style>
      <div className="mbp-stage" style={{ transform: `translate(-50%, -50%) scale(${scale})` }}>
        {/* Background: drawn on the full screen (root), so no side bars on TV */}

        {/* Title (never changes) */}
                <div style={{ position: 'absolute', left: 0, top: 28, width: STAGE_W, textAlign: 'center', fontSize: 54, fontWeight: 900, letterSpacing: -0.8, lineHeight: 1.1 }}>
          NEW! <Rainbow>MagicBeacon</Rainbow>: Wait Time Accessory
        </div>
        <div style={{ position: 'absolute', left: 0, top: 102, width: STAGE_W, textAlign: 'center', fontSize: 44, fontWeight: 700, color: COLORS.pink, lineHeight: 1.1 }}>
          Use Your MagicBands At Home!
        </div>

        {/* Headline + text (pink line and text change per slide) */}
        <div style={{ position: 'absolute', left: 104, top: 232, width: 440 }}>
          <h2 style={{ margin: 0, fontSize: 44, fontWeight: 900, lineHeight: 1.05, letterSpacing: -1.2 }}>
            <span key={'l1-' + slide.id} className={slide.line1 !== 'Your MagicBands.' ? 'mbp-swapLine' : undefined}>{slide.line1}</span><br />
            <span key={'pink-' + slide.id} className="mbp-swapLine" style={{ color: COLORS.pink, marginTop: 8 }}>{fill(slide.pink)}</span>
          </h2>
          <p key={'text-' + slide.id} className="mbp-swapText" style={{ margin: '8px 0 0', width: 440, fontSize: slide.textSize, lineHeight: 1.45, color: '#FFFFFF' }}>
            {fill(slide.text)}
          </p>
        </div>

        {/* Clip (changes per slide) */}
        <div style={{ position: 'absolute', left: 568, top: 228, width: 672, height: 378, borderRadius: 20, border: '3px solid #FFFFFF', background: '#000', overflow: 'hidden' }}>
          {outgoing && (
            <img
              key={'out-' + SLIDES[outgoing.idx].id + '-' + i}
              className={'mbp-clip mbp-clipOut-' + outgoing.dir}
              src={poster(SLIDES[outgoing.idx].clip)}
              alt=""
              draggable={false}
              onAnimationEnd={() => setOutgoing(null)}
            />
          )}
          <LoopVideo
            key={'clip-' + slide.id}
            className={'mbp-clip' + (outgoing ? ' mbp-clipIn-' + dir : '')}
            src={vid(slide.clip)}
            poster={poster(slide.clip)}
            label={slide.alt}
          />
        </div>

        {/* The product, in a circle (never changes) */}
        <div style={{ position: 'absolute', left: 1060, top: 120, width: 200, height: 200, borderRadius: 999 }}>
          {!CIRCLE_ANIMATED ? (
            <img
              src={poster('spin')}
              alt="The MagicBeacon plug-in"
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 999, border: `5px solid ${COLORS.ring}`, background: '#FFFFFF', display: 'block' }}
            />
          ) : (
            <LoopVideo
              src={vid('spin')}
              poster={poster('spin')}
              label="The MagicBeacon plug-in"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 999, border: `5px solid ${COLORS.ring}`, background: '#FFFFFF', display: 'block' }}
            />
          )}
        </div>

        {/* Watch the Demo (never changes) */}
        <button
          type="button"
          className="mbp-btn mbp-press"
          onClick={openVideo}
          style={{ ...ring('demo'), position: 'absolute', left: 104, top: slide.demoTop, transition: 'top .5s cubic-bezier(.2,.7,.2,1), transform .12s ease', width: 300, height: 62, borderRadius: 999, border: '3px solid #FFFFFF', background: 'rgba(18,26,51,.9)', color: '#FFFFFF', fontSize: 23, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, paddingRight: 8 }}
        >
          <span style={{ width: 36, height: 36, borderRadius: 999, background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
            <svg width="14" height="16" viewBox="0 0 20 22" aria-hidden="true"><path d="M4 2l15 9-15 9z" fill="#121A33" /></svg>
          </span>
          Watch the Demo
        </button>

        {/* Arrows + dots, centered under the clip */}
        {hasNext && (
          <div style={{ position: 'absolute', left: 568, top: 620, width: 672, height: 68, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 22 }}>
            <button type="button" className="mbp-btn mbp-press" onClick={goPrev} aria-label="Previous" disabled={!hasPrev}
              style={{ ...ring('prev'), width: 56, height: 56, borderRadius: 999, background: 'rgba(255,255,255,.16)', border: '2px solid rgba(255,255,255,.6)', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: hasPrev ? 1 : 0.3, cursor: hasPrev ? 'pointer' : 'default', transition: 'opacity .3s ease' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
            </button>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} aria-hidden="true">
              {SLIDES.map((sl, k) => <span key={sl.id} className={'mbp-dot' + (k === i ? ' on' : '')} />)}
            </div>
              <button type="button" className="mbp-btn mbp-press mbp-next" onClick={goNext} aria-label="Next feature" style={{ position: 'relative', background: '#FFFFFF', ...ring('next') }}>
              Next Feature
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        )}

        {/* Claim (never changes) */}
        {isTv && focusId === 'claim' && (
                    <div style={{ position: 'absolute', left: notifyOn ? 421 : 451, top: 691, width: notifyOn ? 438 : 378, height: 92, borderRadius: 999, border: `5px solid ${LIME}`, pointerEvents: 'none' }} />
        )}
                  <div className="mbp-claimWrap" style={{ left: notifyOn ? 430 : 460, top: 700, width: notifyOn ? 420 : 360, height: 74 }}>
                    <button type="button" className="mbp-btn mbp-claim mbp-press" onClick={handleClaimBtn} style={notifyOn ? { fontSize: 22 } : undefined}>
                  {notifyOn ? (earlyUsed ? 'Early Access: Claim My Beacon!' : 'PRE-ORDER: NOTIFY ME!') : 'Claim My MagicBeacon'}
          </button>
        </div>

        {/* Close → Home (same as BeaconComingDisplay's EXIT) */}
        {!showVideo && (
          <button type="button" className="mbp-btn mbp-press" onClick={handleClose} aria-label="Close"
            style={{ ...ring('close'), position: 'absolute', left: 1198, top: 22, width: 56, height: 56, borderRadius: 999, background: 'rgba(23,26,44,.9)', border: isTv ? '3px solid #FFFFFF' : '1px solid #3A3D57', color: '#F4F2EE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
                )}

        {/* Secret Sale unlock overlay */}
        {showUnlock && (
          <div className="mbp-un-root">
            <div className="mbp-un-rays" />
            <div className="mbp-un-lock">
              <svg width="150" height="205" viewBox="0 -24 120 164" style={{ overflow: 'visible' }} aria-hidden="true">
                <path className="mbp-un-shackle" d="M32 64V42a28 28 0 0 1 56 0v22" fill="none" stroke="#FFD23D" strokeWidth="12" strokeLinecap="round" />
                <rect x="14" y="60" width="92" height="74" rx="16" fill="#FFD23D" />
                <circle cx="60" cy="92" r="10" fill="#0A0B14" />
                <rect x="55" y="96" width="10" height="20" rx="4" fill="#0A0B14" />
              </svg>
            </div>
            {SPARKS.map((s, k) => (
              <span key={k} className="mbp-un-spark" style={{ '--dx': s.dx + 'px', '--dy': s.dy + 'px', background: s.c, animationDelay: (2.2 + s.d) + 's' }} />
            ))}
            <div className="mbp-un-card">
              <div className="mbp-un-title">SECRET SALE UNLOCKED!</div>
                            <div className="mbp-un-sub">You unlocked <b style={{ color: COLORS.pink }}>early access</b> to order your MagicBeacon <b>TODAY</b> before everyone else!</div>
              <div style={{ marginTop: 26, fontSize: 26, fontWeight: 900, color: COLORS.pink }}>IMPORTANT: Due to chip shortages, inventory is limited.</div>
                            <div style={{ position: 'relative', display: 'inline-block', marginTop: 40 }}>
                {isTv && focusId === 'ushop' && (
                  <div style={{ position: 'absolute', left: -9, top: -9, width: 398, height: 102, borderRadius: 999, border: `5px solid ${LIME}`, pointerEvents: 'none', zIndex: 2 }} />
                )}
                <div className="mbp-claimWrap" style={{ position: 'relative', width: 380, height: 84 }}>
                  <button type="button" className="mbp-btn mbp-claim mbp-press" style={{ fontSize: 30 }} onClick={handleUnlockShop}>
                    CLAIM MY BEACON!
                  </button>
                                </div>
              </div>
              <div>
                <button type="button" className="mbp-btn mbp-press mbp-un-no" onClick={closeUnlock} style={ring('uclose')}>
                  No thanks, I’ll wait for the release
                </button>
              </div>
            </div>
            <div className="mbp-un-flash" />
            
          </div>
        )}
      </div>

      {/* Demo video — identical behavior to BeaconComingDisplay */}
      {showVideo && (
        <div className="mbp-video-root">
          <button
            className="mbp-video-close"
            onClick={closeVideo}
            aria-label="Close video"
            style={isTv ? { border: `5px solid ${focusId === 'vclose' ? LIME : 'transparent'}`, borderRadius: 16, padding: '0 14px', lineHeight: 1.1 } : undefined}
          >
            ✕
          </button>
            <div className="mbp-video-card" style={isTv ? { width: "min(92vw, 160vh)", maxWidth: "none", maxHeight: "none" } : undefined}>
            <video
              key="beacon-promo-video"
              ref={videoRef}
              className="mbp-video"
              controls={!isTv}
              autoPlay
              playsInline
              preload="auto"
              controlsList="nodownload nofullscreen"
              disablePictureInPicture
              onPlaying={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={closeVideo}
              onError={() => {
                try {
                  const v = videoRef.current;
                  if (!v) return;
                  v.muted = true;
                  v.load();
                  v.play().catch(() => {});
                  setMutedFallback(true);
                } catch {}
              }}
            >
              <source src={isTv ? DEMO_VIDEO_URL_TV : DEMO_VIDEO_URL} type="video/mp4" />
            </video>

            {isPlaying && mutedFallback && (
              <button
                className="mbp-unmute-btn"
                onClick={() => {
                  const v = videoRef.current;
                  if (!v) return;
                  try {
                    v.muted = false;
                    v.volume = 0.5;
                    const p = v.play();
                    if (p && typeof p.then === "function") p.catch(() => {});
                    setMutedFallback(false);
                  } catch {}
                }}
              >
                Tap to Unmute
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
